/**
 * Content Guard — Tier 1 local pattern matching for compliance.
 * Zero AI cost. Runs on every message before sending.
 *
 * CONTACT SHARING POLICY — CONFIRM, NEVER BLOCK (Fahad, 2026-08-08)
 * ----------------------------------------------------------------
 * Sharing a phone number / email / address in chat is NEVER hard-blocked, at
 * any relationship stage. Once someone has applied to a job and the employer is
 * already messaging or calling them in-app, the relationship is legitimate and
 * exchanging contact details creates no platform risk — both sides stay
 * subscribed either way. Instead the UI shows a cautionary confirm step
 * ("Behtari can't verify or protect what happens off-platform") and sends only
 * if the sender confirms.
 *
 * This replaced a hard block that shipped on 2026-08-06. Note the history, because
 * both previous states were wrong: before that the relationship lookup was a
 * malformed PostgREST query whose catch set `isApprovedRelationship = true`, so
 * the block was silently fail-open and never fired at all. Fixing the query
 * turned it into a real block for ~80% of direct conversations, which is what
 * surfaced the policy question.
 *
 * WHAT IS DELIBERATELY KEPT — do not remove these when touching the above:
 *  - The risk SCORES are unchanged. A pre-hire contact share still scores 8 and
 *    crosses AI_RISK_THRESHOLD, so it is still escalated to Tier-2 AI review and
 *    still lands in the admin moderation surfaces. Confirm-not-block removes the
 *    barrier, not the oversight.
 *  - Rate limiting (20/60s), the support-thread exemption, profanity/threat and
 *    spam scoring are untouched by this policy.
 *
 * `shouldBlock` is consequently never set to true by this function today. The
 * field and its plumbing are retained on purpose so a future rule (e.g. a CSAM
 * or credible-threat rule) can block without re-plumbing every caller.
 */

// Pakistani phone patterns: 03XX-XXXXXXX, +923XXXXXXXXX, 0092-3XX...
const PHONE_PATTERNS = [
  /(?:0|\+92|0092)\s*[-.]?\s*3\s*\d{2}\s*[-.]?\s*\d{7}/g,
  /\b03\d{2}[-.\s]?\d{7}\b/g,
  /\+92[-.\s]?3\d{2}[-.\s]?\d{7}/g,
  /0092[-.\s]?3\d{2}[-.\s]?\d{7}/g,
  // Landline patterns
  /\b0\d{2,3}[-.\s]?\d{7,8}\b/g,
];

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;

// Address patterns — street numbers, house numbers, plot numbers
const ADDRESS_PATTERNS = [
  /(?:house|plot|flat|apartment|apt|ghar|makan)\s*(?:no\.?|#|number)?\s*\d+/gi,
  /(?:street|gali|lane|road|sector)\s*(?:no\.?|#)?\s*\d+/gi,
  /\b\d+[-/]\w+\s+(?:street|road|colony|town|society|phase|block)\b/gi,
];

// Profanity / harassment keywords — English + Urdu transliteration
// Kept intentionally short and focused on clearly offensive terms
//
// MATCHING IS WORD-BOUNDARY, NOT SUBSTRING (fixed 2026-08-08)
// -----------------------------------------------------------
// These lists used to be tested with `normalized.includes(word)`, so every entry
// matched anywhere inside a longer word. `'sala'` therefore flagged "Salam",
// "Salaam" and "Assalam o alaikum" — the single most common opening line on the
// platform. Verified live before the fix: of the messages containing `sala` as a
// substring, **100% were greetings and none were the actual slur**. `'rape'` had
// the same defect in English ("grape", "drape", "scrape").
//
// A trailing `*` marks a STEM that may carry a short inflectional suffix
// (`\bstem[a-z]{0,3}\b`). That is what keeps "fucking", "chutiye", "gaandu" and
// "retarded" matching now that bare substring matching is gone. Entries WITHOUT
// a `*` must match as a whole word only — `'sala'` deliberately has no `*`, so
// the slur still flags on its own while "salam" never can.
const PROFANITY_KEYWORDS_EN = [
  'fuck*', 'shit*', 'bitch*', 'bastard*', 'asshole*', 'dick*', 'pussy*',
  'whore*', 'slut*', 'nigger*', 'faggot*', 'retard*', 'cunt*',
  'kill you', 'murder you', 'rape', 'raped', 'raping', 'i will hurt',
];

const PROFANITY_KEYWORDS_UR = [
  'madarchod*', 'bhenchod*', 'chutiy*', 'gandu*', 'harami*', 'haram khor',
  'randi*', 'kanjar*', 'dalla', 'bhosdike', 'lora', 'lund*', 'gaand*',
  'kutti*', 'haramzad*', 'sala', 'kamina', 'kamini',
  'tatto', 'choot*', 'behen ke', 'maa ki', 'teri maa',
  'maar dunga', 'jaan se maar', 'qatal', 'zina',
];

// Regional languages: Pashto, Sindhi (transliterated) + common cross-dialect terms
const PROFANITY_KEYWORDS_REGIONAL = [
  // Punjabi
  'penchod*', 'kutti*', 'khotay da', 'hathi da', 'ghashti', 'bhand',
  'kanjri', 'chootad', 'pulla', 'takla',
  // Pashto
  'khaista', 'ghul', 'zanai', 'badmash', 'haram zoy',
  'naray mada', 'spay', 'ghal ghala',
  // Sindhi
  'bhudkau', 'bhungar', 'chhinal', 'randwa', 'suwar ja',
  'haramjado', 'kutri', 'gandasi',
];

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Compile a keyword list into one word-boundary regex.
 * `word`  -> \bword\b            (exact word only)
 * `word*` -> \bword[a-z]{0,3}\b  (word plus a short inflectional suffix)
 */
function buildKeywordMatcher(words: readonly string[]): RegExp {
  const alternatives = words.map((w) => {
    const isStem = w.endsWith('*');
    const body = escapeRegex(isStem ? w.slice(0, -1) : w);
    return isStem ? `${body}[a-z]{0,3}` : body;
  });
  return new RegExp(`\\b(?:${alternatives.join('|')})\\b`, 'i');
}

const PROFANITY_RE_EN = buildKeywordMatcher(PROFANITY_KEYWORDS_EN);
const PROFANITY_RE_UR = buildKeywordMatcher(PROFANITY_KEYWORDS_UR);
const PROFANITY_RE_REGIONAL = buildKeywordMatcher(PROFANITY_KEYWORDS_REGIONAL);

// Scam / system-gaming patterns
const SCAM_PATTERNS = [
  /(?:send|transfer|deposit)\s*(?:money|paisa|rupee|rs\.?)\s*(?:to|on|in)\s*(?:my|this)/gi,
  /(?:easypaisa|jazzcash|bank\s*account)\s*(?:number|no\.?|#)\s*:?\s*\d+/gi,
  /(?:meet|milo|aao)\s*(?:outside|bahar|personally|akele)/gi,
  /whatsapp\s*(?:pe|par|on|number|no)/gi,
  /(?:call|contact)\s*(?:me|mujhe)\s*(?:on|pe|par)\s*(?:this|ye|is)/gi,
];

// Spam: excessive caps, repeated messages, link spam
const LINK_PATTERN = /https?:\/\/[^\s]+/gi;
const ALL_CAPS_THRESHOLD = 0.7; // 70% of chars are uppercase
const MIN_CAPS_LENGTH = 20;

export interface ContentGuardResult {
  riskScore: number; // 0-10
  flags: string[];
  shouldBlock: boolean;
  blockReason: string | null;
  categories: string[];
  /**
   * The message shares contact details (phone / email / address) or names an
   * off-platform contact channel. The UI must ask the sender to confirm before
   * sending — it must NOT block. See CONTACT SHARING POLICY below.
   */
  requiresContactConfirmation: boolean;
  contactConfirmationReason: string | null;
}

interface ContentGuardOptions {
  /** Whether the sender has an approved/hired relationship with receiver */
  isApprovedRelationship: boolean;
  /** Previous messages from this user in this conversation (for spam detection) */
  recentMessages?: string[];
}

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[٠-٩]/g, (c) => String(c.charCodeAt(0) - 0x0660).toString())
    .replace(/\s+/g, ' ')
    .trim();
}

function detectContactLeakage(text: string): string[] {
  const flags: string[] = [];
  
  for (const pattern of PHONE_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      flags.push('phone_number_detected');
      break;
    }
  }
  
  EMAIL_PATTERN.lastIndex = 0;
  if (EMAIL_PATTERN.test(text)) {
    flags.push('email_detected');
  }
  
  for (const pattern of ADDRESS_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      flags.push('address_detected');
      break;
    }
  }
  
  return flags;
}

function detectProfanity(text: string): string[] {
  const flags: string[] = [];
  const normalized = normalizeText(text);
  
  if (PROFANITY_RE_EN.test(normalized)) flags.push('profanity_en');
  if (PROFANITY_RE_UR.test(normalized)) flags.push('profanity_ur');
  if (PROFANITY_RE_REGIONAL.test(normalized)) flags.push('profanity_regional');

  // Threat detection. `rape` is word-boundary anchored for the same reason as
  // the keyword lists above — the bare /rape/ it replaced matched "grape".
  const threatPatterns = [/kill\s*you/i, /maar\s*dunga/i, /jaan\s*se/i, /\brap(?:e|ed|ing|ist)\b/i];
  for (const p of threatPatterns) {
    if (p.test(text)) {
      flags.push('threat_detected');
      break;
    }
  }
  
  return flags;
}

function detectScamPatterns(text: string): string[] {
  const flags: string[] = [];
  
  for (const pattern of SCAM_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) {
      flags.push('contact_sharing_attempt');
      break;
    }
  }
  
  return flags;
}

function detectSpam(text: string, recentMessages?: string[]): string[] {
  const flags: string[] = [];
  
  // Excessive links
  LINK_PATTERN.lastIndex = 0;
  const links = text.match(LINK_PATTERN);
  if (links && links.length >= 3) {
    flags.push('excessive_links');
  }
  
  // ALL CAPS
  const letters = text.replace(/[^a-zA-Z]/g, '');
  if (letters.length >= MIN_CAPS_LENGTH) {
    const upperCount = (text.match(/[A-Z]/g) || []).length;
    if (upperCount / letters.length > ALL_CAPS_THRESHOLD) {
      flags.push('all_caps_spam');
    }
  }
  
  // Repeated messages
  if (recentMessages && recentMessages.length >= 3) {
    const normalized = normalizeText(text);
    const duplicates = recentMessages.filter(m => normalizeText(m) === normalized).length;
    if (duplicates >= 2) {
      flags.push('repeated_message');
    }
  }
  
  return flags;
}

/**
 * Analyze message content for compliance issues.
 * Returns risk score, flags, and whether to block.
 */
export function analyzeContentLocally(
  content: string,
  options: ContentGuardOptions
): ContentGuardResult {
  const flags: string[] = [];
  const categories: string[] = [];
  let riskScore = 0;
  const shouldBlock = false;
  const blockReason: string | null = null;
  let requiresContactConfirmation = false;
  let contactConfirmationReason: string | null = null;

  if (!content || content.trim().length === 0) {
    return {
      riskScore: 0, flags: [], shouldBlock: false, blockReason: null, categories: [],
      requiresContactConfirmation: false, contactConfirmationReason: null,
    };
  }
  
  // 1. Contact leakage detection
  const contactFlags = detectContactLeakage(content);
  if (contactFlags.length > 0) {
    flags.push(...contactFlags);
    categories.push('contact_leakage');

    if (!options.isApprovedRelationship) {
      // CONFIRM, never block — see CONTACT SHARING POLICY at the top of the
      // file. The risk score is unchanged, so a pre-hire contact share still
      // crosses AI_RISK_THRESHOLD and is escalated for Tier-2 AI review.
      requiresContactConfirmation = true;
      contactConfirmationReason = contactFlags.includes('phone_number_detected')
        ? 'phone number'
        : contactFlags.includes('email_detected')
        ? 'email address'
        : 'address';
      riskScore += 8;
    } else {
      riskScore += 1; // Approved relationship — contact sharing is expected
    }
  }

  // 2. Scam / system gaming
  const scamFlags = detectScamPatterns(content);
  if (scamFlags.length > 0) {
    flags.push(...scamFlags);
    categories.push('system_gaming');

    if (!options.isApprovedRelationship) {
      // Also confirm-not-block. These patterns emit `contact_sharing_attempt`
      // and overlap heavily with the phrasing people use around a real number
      // ("call me on this", "whatsapp pe"), so leaving them as a hard block
      // would still hard-block ordinary contact sharing and defeat the policy.
      requiresContactConfirmation = true;
      contactConfirmationReason = contactConfirmationReason ?? 'contact details';
      riskScore += 7;
    } else {
      riskScore += 2;
    }
  }
  
  // 3. Profanity / threats
  const profanityFlags = detectProfanity(content);
  if (profanityFlags.length > 0) {
    flags.push(...profanityFlags);
    categories.push('profanity');
    riskScore += profanityFlags.includes('threat_detected') ? 6 : 4;
  }
  
  // 4. Spam patterns
  const spamFlags = detectSpam(content, options.recentMessages);
  if (spamFlags.length > 0) {
    flags.push(...spamFlags);
    categories.push('spam');
    riskScore += 2;
  }
  
  // Cap at 10
  riskScore = Math.min(riskScore, 10);
  
  return {
    riskScore,
    flags: [...new Set(flags)],
    shouldBlock,
    blockReason,
    categories: [...new Set(categories)],
    requiresContactConfirmation,
    contactConfirmationReason,
  };
}

/**
 * Synchronous, DB-free contact-share check for the UI confirm step.
 *
 * Deliberately separate from `analyzeContentLocally`: that function needs
 * `isApprovedRelationship`, which costs DB round trips, and the confirmation is
 * shown REGARDLESS of relationship stage — so the UI must not pay for a lookup
 * whose answer it does not use. `analyzeContentLocally` still runs server-side
 * of this, in messagesService, for scoring and Tier-2 escalation.
 */
export function detectContactShare(content: string): {
  detected: boolean;
  reason: string | null;
} {
  if (!content || !content.trim()) return { detected: false, reason: null };

  const contactFlags = detectContactLeakage(content);
  if (contactFlags.includes('phone_number_detected')) return { detected: true, reason: 'phone number' };
  if (contactFlags.includes('email_detected')) return { detected: true, reason: 'email address' };
  if (contactFlags.includes('address_detected')) return { detected: true, reason: 'address' };
  if (detectScamPatterns(content).length > 0) return { detected: true, reason: 'contact details' };

  return { detected: false, reason: null };
}

/** Risk threshold above which AI analysis is triggered */
export const AI_RISK_THRESHOLD = 3;
